import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StyledAlertProvider } from './components/ui/StyledAlert';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { brandAssets } from './utils/cavAssets';
import { API_BASE_URL } from './api/config';
import { GlobalLoadingProgress } from './components/ui/GlobalLoadingProgress';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { useGlobalLoadingProgressSignal } from './utils/globalLoadingProgress';

const AdminDashboard = lazy(() => lazyWithRetry(() => import('./pages/AdminDashboard')));
const LandingPage = lazy(() => lazyWithRetry(() => import('./pages/LandingPage')));
const BookingGuidePage = lazy(() => lazyWithRetry(() => import('./pages/BookingGuidePage')));
const LoginPage = lazy(() => lazyWithRetry(() => import('./pages/LoginPage')));
const PrivacyPolicyPage = lazy(() => lazyWithRetry(() => import('./pages/PrivacyPolicyPage')));
const NotFoundPage = lazy(() => lazyWithRetry(() => import('./pages/NotFoundPage')));
const CustomerDashboard = lazy(() => lazyWithRetry(() => import('./pages/CustomerDashboard')));
const BookingFeedbackPage = lazy(() => lazyWithRetry(() => import('./pages/BookingFeedbackPage')));
const StaffDashboard = lazy(() => lazyWithRetry(() => import('./pages/StaffDashboard')));
const MachineLearningPage = lazy(() => lazyWithRetry(() => import('./pages/MachineLearningPage')));
const SPLASH_SEEN_KEY = 'cav:splash-seen';

const redactDiagnostic = (value, limit) => String(value || '')
  .replace(/Bearer\s+[^\s]+/gi, 'Bearer [redacted]')
  .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[redacted token]')
  .slice(0, limit);

const reportClientRuntimeError = (error, errorInfo) => {
  if (typeof window === 'undefined') return;

  let savedUser = '';
  try {
    savedUser = localStorage.getItem('user') || '';
  } catch {
    return;
  }
  if (!savedUser) return;

  const csrfToken = document.cookie.split('; ').find(item => item.startsWith('csrftoken='))?.split('=').slice(1).join('=') || '';

  const payload = {
    message: redactDiagnostic(error?.message || error, 500),
    component_stack: redactDiagnostic(errorInfo?.componentStack, 4000),
    route: window.location.pathname,
  };

  window.fetch(`${API_BASE_URL}/api/audit/client-runtime-errors/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(csrfToken ? { 'X-CSRFToken': decodeURIComponent(csrfToken) } : {}) },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Browser diagnostics must never affect the running application.
  });
};

function AppLoader() {
  useGlobalLoadingProgressSignal(true);
  return (
    <div className="min-h-screen bg-cream" aria-busy="true" aria-label="Loading page" />
  );
}

function SplashScreen() {
  return (
    <div className="min-h-screen overflow-hidden bg-espresso flex items-center justify-center px-6 text-cream">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(212,175,55,0.16),transparent_38%,rgba(255,255,255,0.05))]" />
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center text-center">
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-[28px] bg-gold/20 blur-2xl animate-pulse" />
          <div className="relative flex h-24 w-24 items-center justify-center rounded-[28px] border border-gold/25 bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)] sm:h-28 sm:w-28">
            <img src={brandAssets.logo} alt="CAV logo" className="h-16 w-16 rounded-2xl object-cover sm:h-20 sm:w-20" />
          </div>
        </div>

        <p className="text-[11px] font-black uppercase tracking-[0.28em] text-gold">Photo Studio & Cafe</p>
        <h1 className="mt-2 font-sans text-4xl font-black tracking-normal text-white sm:text-5xl">CAV</h1>
        <p className="mt-3 max-w-xs text-sm leading-relaxed text-cream/68">
          Preparing your studio, cafe, and booking space.
        </p>

        <div className="mt-8 flex items-center gap-2" aria-label="Loading">
          {[0, 1, 2].map((item) => (
            <span
              key={item}
              className="h-2.5 w-2.5 rounded-full bg-gold animate-bounce"
              style={{ animationDelay: `${item * 0.16}s` }}
            />
          ))}
        </div>
        <div className="mt-5 h-1.5 w-44 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-full origin-left rounded-full bg-gold animate-[splashProgress_2.4s_ease-in-out_forwards]" />
        </div>
      </div>
    </div>
  );
}

function StartupSplashGate({ children }) {
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem(SPLASH_SEEN_KEY) !== 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!showSplash || location.pathname !== '/') return undefined;
    const timer = window.setTimeout(() => {
      try {
        sessionStorage.setItem(SPLASH_SEEN_KEY, 'true');
      } catch {
        /* ignore private browsing storage failures */
      }
      setShowSplash(false);
    }, 2400);
    return () => window.clearTimeout(timer);
  }, [location.pathname, showSplash]);

  return showSplash && location.pathname === '/' ? <SplashScreen /> : children;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'ADMIN') return <Navigate to="/admin" replace />;
    if (user.role === 'STAFF') return <Navigate to="/staff" replace />;
    return <Navigate to="/customer" replace />;
  }

  return children;
}

function AppErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary
      resetKey={`${location.pathname}${location.search}`}
      onError={reportClientRuntimeError}
    >
      <React.Fragment key={location.key}>{children}</React.Fragment>
    </RouteErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <StyledAlertProvider>
        <BrowserRouter>
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <AppErrorBoundary>
            <StartupSplashGate>
              <GlobalLoadingProgress />
              <Suspense fallback={<AppLoader />}>
                <div id="main-content" tabIndex="-1">
                <Routes>
                  <Route path="/" element={<LandingPage page="home" />} />
                  <Route path="/photo-studio" element={<LandingPage page="studio" />} />
                  <Route path="/cafe-menu" element={<LandingPage page="cafe" />} />
                  <Route path="/gallery" element={<LandingPage page="gallery" />} />
                  <Route path="/our-story" element={<LandingPage page="story" />} />
                  <Route path="/how-to-book" element={<BookingGuidePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/booking-feedback" element={<BookingFeedbackPage />} />
                  <Route
                    path="/customer"
                    element={
                      <ProtectedRoute allowedRoles={['CUSTOMER']}>
                        <CustomerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/staff"
                    element={
                      <ProtectedRoute allowedRoles={['STAFF', 'ADMIN']}>
                        <StaffDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={['ADMIN']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/machine-learning"
                    element={
                      <ProtectedRoute allowedRoles={['STAFF', 'ADMIN']}>
                        <MachineLearningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </div>
              </Suspense>
            </StartupSplashGate>
          </AppErrorBoundary>
        </BrowserRouter>
      </StyledAlertProvider>
    </AuthProvider>
  );
}

export default App;
